/**
 * SOLID Review
 * S — Strong. One concern: the room a seat is taken at, built from the server's answer — a table joined, a stored
 *     game's table re-joined, a table just created.
 * O — Good. A new way to a seat is one more factory.
 * L — N/A — no inheritance hierarchy (private constructor).
 * I — Strong. Three factories and the snapshot.
 * D — Strong. The table's seats and the schema types.
 */
import { Roster } from './Roster';
import type { IEntityClientRoom } from '../ports/schemas/ClientTable';
import type { IEntityClientResume } from '../ports/schemas/ClientSession';
import type { IPayloadSeatRoom } from '../ports/schemas/SessionMoves';

/** A room as `joinRoom`/`lateJoinRoom` answer it, or a rejoin's — the old `buildRoomEntity`. */
export interface IEntityRoomSource {
  readonly id: string;
  readonly roomCode: string;
  readonly playerIds?: ReadonlyArray<string>;
  readonly players: ReadonlyArray<{ readonly id: string; readonly displayName: string }>;
  readonly status: string;
}

/** Every table seats two to six (the backend's rule; the client only displays it). */
const MIN_PLAYERS = 2;
const MAX_PLAYERS = 6;

/**
 * THE ROOM A SEAT IS TAKEN AT (frontend-domain-oo P4, 2026-10-26; handbook §28.271) — the three builders the
 * session's draft held (`joinedRoom`, `restoredRoom`, `hostedRoom` in `decisions/session/sessionDraft.ts`), each
 * the hook or panel it was moved from in FE2. Every seat is fresh from the server's list (`Roster.fresh`); the table
 * takes the room as built (`ClientTable.sitAt`).
 */
export class TableRoom {
  private constructor(private readonly room: IEntityClientRoom) {}

  /**
   * A table joined by code, seen from `me` (application/mappers/buildRoomEntity.ts, moved). The host is the first
   * listed seat, or this player when none is listed.
   */
  static joined(raw: IEntityRoomSource, me: string): TableRoom {
    const room: IEntityClientRoom = {
      roomId: raw.id,
      roomCode: raw.roomCode,
      hostPlayerId: (raw.playerIds ?? raw.players.map((p) => p.id))[0] ?? me,
      isGameActive: raw.status === 'IN_PROGRESS',
      minPlayers: MIN_PLAYERS,
      maxPlayers: MAX_PLAYERS,
      players: Roster.fresh(raw.players, me).toSnapshot(),
    };
    return new TableRoom(room);
  }

  /**
   * A stored game's table, re-joined (useGameStateRestore's room). The record fills what the answer lacks: its code,
   * and the host when no seat is listed.
   */
  static restored(raw: IPayloadSeatRoom, record: IEntityClientResume): TableRoom {
    const room: IEntityClientRoom = {
      roomId: raw.id,
      hostPlayerId: raw.playerIds[0] ?? record.playerId,
      roomCode: raw.roomCode || record.roomCode,
      isGameActive: raw.status === 'IN_PROGRESS',
      minPlayers: MIN_PLAYERS,
      maxPlayers: MAX_PLAYERS,
      players: Roster.fresh(raw.players, record.playerId).toSnapshot(),
    };
    return new TableRoom(room);
  }

  /**
   * A table this player just created (MainMenuPanel's room): theirs to host, not yet started. The code is the
   * server's; a missing one is left blank, never derived from the id — a derived code looks real and joins nothing.
   */
  static hosted(raw: IPayloadSeatRoom, host: string): TableRoom {
    const room: IEntityClientRoom = {
      roomId: raw.id,
      roomCode: raw.roomCode,
      hostPlayerId: host,
      maxPlayers: MAX_PLAYERS,
      minPlayers: MIN_PLAYERS,
      isGameActive: false,
      players: Roster.fresh(raw.players, host).toSnapshot(),
    };
    return new TableRoom(room);
  }

  toSnapshot(): IEntityClientRoom {
    return this.room;
  }
}
